import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Form, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { authAPI, avatarsAPI } from '../services/api';
import { extractGoogleId, extractGoogleUserInfo } from '../utils/googleAuth';
import './SignupPage.css';

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [avatarType, setAvatarType] = useState('for myself'); // Default to free signup
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const googleInitialized = useRef(false);
  const errorShownRef = useRef(false);
  const isSubmittingRef = useRef(false); // Prevent duplicate form submissions
  const lastSubmissionTimeRef = useRef(0); // Track last submission time for debounce
  
  // Google OAuth Client ID - should be set in environment variables
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  // Get invite_id, invite_email, i_will_pay from URL query parameters
  const inviteId = searchParams.get('invite_id');
  const inviteEmail = searchParams.get('invite_email') ? decodeURIComponent(searchParams.get('invite_email')) : '';
  const iWillPay = searchParams.get('i_will_pay');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: inviteEmail || '',
      password: ''
    }
  });

  // Auto-fill email from URL parameter and make it read-only if invite_email is present
  useEffect(() => {
    if (inviteEmail) {
      setValue('email', inviteEmail);
    }
  }, [inviteEmail, setValue]);

  // Store invite_id, invite_email, i_will_pay in localStorage when present in URL
  useEffect(() => {
    if (inviteId || inviteEmail || iWillPay != null) {
      const inviteParams = {};
      if (inviteId) inviteParams.invite_id = inviteId;
      if (inviteEmail) inviteParams.invite_email = inviteEmail;
      if (iWillPay != null) inviteParams.i_will_pay = iWillPay;
      localStorage.setItem('signup_invite_params', JSON.stringify(inviteParams));
    }
  }, [inviteId, inviteEmail, iWillPay]);

  // Handle Google credential response
  const handleGoogleCredential = useCallback(async (response) => {
    console.log('Google OAuth callback received for SignUp');
    
    if (!response?.credential) {
      toast.error('Failed to get Google credential');
      return;
    }

    setLoading(true);
    
    try {
      // Extract google_id (sub) from the ID token
      const googleId = extractGoogleId(response.credential);
      
      // Extract user info from the token
      const userInfo = extractGoogleUserInfo(response.credential);
      
      if (!userInfo.email) {
        throw new Error('Failed to decode Google credential or email not provided');
      }
      
      // Convert avatar_type string to number (1 for 'for myself', 2 for 'for a loved one')
      // Default to free signup (for myself)
      const avatarTypeNumber = 1; // Always free signup
      
      // Prepare signup data matching the register API format
      const signupData = {
        google_id: googleId,
        email: userInfo.email,
        first_name: userInfo.first_name || userInfo.name?.split(' ')[0] || 'Google',
        last_name: userInfo.last_name || userInfo.name?.split(' ').slice(1).join(' ') || '',
        auth_provider: 'google',
        avatar_type: avatarTypeNumber, // Always 1 (for myself - free)
      };

      // Include invite_id in payload if present in URL
      if (inviteId) {
        signupData.invite_id = inviteId;
        console.log('Including invite_id in Google signup payload:', inviteId);
      }

      // Call backend API to register
      const apiResponse = await authAPI.googleSignup(signupData);
      
      if (apiResponse.status) {
        toast.success('Account created successfully with Google!');
        
        // After successful signup, automatically log the user in to get tokens
        try {
          const loginResponse = await authAPI.googleSignin({
            email: userInfo.email,
            google_id: googleId
          });

          // Handle login response structure:
          // Direct object: { access_token, refresh_token, token_type, user }
          // Or wrapped: { status, data: { access_token, refresh_token, user } }
          const accessToken = loginResponse.access_token || loginResponse.data?.access_token;
          const refreshToken = loginResponse.refresh_token || loginResponse.data?.refresh_token;
          const userData = loginResponse.user || loginResponse.data?.user;

          if (accessToken) {
            // Store tokens in localStorage
            localStorage.setItem('access_token', accessToken);
            if (refreshToken) {
              localStorage.setItem('refresh_token', refreshToken);
            }
            // Clear chat-related localStorage (user-specific; prevents 403 when new user signs up)
            localStorage.removeItem('chat_session_id');
            localStorage.removeItem('chat_session_project_id');
            localStorage.removeItem('chat_messages');
            
            // Store user data if available in response
            if (userData) {
              try {
                sessionStorage.setItem('user_data', JSON.stringify(userData));
                if (userData.id) {
                  sessionStorage.setItem('user_id', userData.id.toString());
                }
              } catch (error) {
                console.warn('Could not store user data in sessionStorage:', error);
              }
            }
            // Clear any previous user's chat session so new user doesn't get 403 on history
            localStorage.removeItem('chat_session_id');
            localStorage.removeItem('chat_messages');
            localStorage.removeItem('chat_session_project_id');
            
            // Check if user has avatars and redirect to avatar details page
            try {
              const userResponse = await authAPI.getCurrentUser();
              const userId = userResponse.data?.id || userResponse.data?.user_id || userData?.id;
              
              if (userId) {
                const avatarsResponse = await avatarsAPI.getAvatars(userId);
                const avatars = avatarsResponse.data?.avatars || [];
                
                if (avatars.length > 0) {
                  navigate('/chat');
                } else {
                  navigate('/create-avatar');
                }
              } else {
                navigate('/create-avatar');
              }
            } catch (error) {
              console.error('Error checking avatars:', error);
              navigate('/create-avatar');
            }
          } else {
            // If no access token from login, still try to navigate
            console.warn('No access token received after Google signup login');
            navigate('/create-avatar');
          }
        } catch (loginError) {
          console.error('Error logging in after Google signup:', loginError);
          // Even if login fails, navigate to create avatar
          // User can sign in manually if needed
          toast.warning('Account created, but automatic sign-in failed. Please sign in manually.');
          navigate('/signin');
        }
      }
    } catch (error) {
      console.error('Google signup error:', error);
      const errorMessage = error.message || 'Google signup failed. Please try again.';
      
      // Check if user already exists
      if (errorMessage.includes('already') || errorMessage.includes('exists')) {
        toast.error('Account already exists. Please sign in instead.', {
          toastId: 'google-user-exists',
        });
      } else {
        toast.error(errorMessage, {
          toastId: 'google-signup-error',
        });
      }
    } finally {
      setLoading(false);
    }
    }, [navigate]);

  const onSubmit = async (data) => {
    // Prevent duplicate submissions (React StrictMode or double-click)
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTimeRef.current;
    
    // Check if already submitting to prevent duplicates
    if (isSubmittingRef.current || loading) {
      console.log('SignupPage: Form submission already in progress, skipping duplicate call');
      return;
    }
    
    // Prevent submissions within 1 second of each other (debounce to prevent accidental double-clicks)
    if (timeSinceLastSubmission < 1000 && lastSubmissionTimeRef.current > 0) {
      console.log('SignupPage: Submission too soon after previous attempt, skipping');
      return;
    }

    // Set all protection flags immediately (synchronous) before any async operations
    isSubmittingRef.current = true;
    lastSubmissionTimeRef.current = now;
    setLoading(true);
    
    try {
      // Always use free signup (for myself - avatar_type = 1)
      // Use inviteEmail when from invite link (disabled field is excluded from form data)
      const registerData = {
        email: inviteEmail || data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        auth_provider: 'local',
        avatar_type: 1, // Always 1 (for myself - free signup)
      };

      // Include invite_id in payload if present in URL
      if (inviteId) {
        registerData.invite_id = inviteId;
        console.log('Including invite_id in signup payload:', inviteId);
      }

      console.log('SignupPage: Calling register API (first time only)...');
      console.log('SignupPage: Registration data:', {
        ...registerData,
        password: '***' // Don't log password
      });
      
      const response = await authAPI.register(registerData);
      
      if (response.status) {
        toast.success('Account created successfully!');
        
        // Auto-login after successful registration
        try {
          const loginResponse = await authAPI.login({
            email: registerData.email,
            password: registerData.password
          });
          
          const accessToken = loginResponse.access_token || loginResponse.data?.access_token;
          const refreshToken = loginResponse.refresh_token || loginResponse.data?.refresh_token;
          const userData = loginResponse.user || loginResponse.data?.user;

          if (accessToken) {
            localStorage.setItem('access_token', accessToken);
            if (refreshToken) {
              localStorage.setItem('refresh_token', refreshToken);
            }
            // Clear any previous chat sessions
            localStorage.removeItem('chat_session_id');
            localStorage.removeItem('chat_session_project_id');
            localStorage.removeItem('chat_messages');
            
            if (userData) {
              try {
                sessionStorage.setItem('user_data', JSON.stringify(userData));
                if (userData.id) {
                  sessionStorage.setItem('user_id', userData.id.toString());
                }
              } catch (error) {
                console.warn('Could not store user data in sessionStorage:', error);
              }
            }
            
            // Navigate directly to avatar creation
            navigate('/create-avatar');
            return; // Exit early on success
          }
        } catch (loginError) {
          console.error('Error auto-logging in after registration:', loginError);
          toast.warning('Account created, but automatic sign-in failed. Please sign in manually.');
        }

        // Don't reset isSubmittingRef on success - prevent any subsequent calls
        navigate('/signin');
        return; // Exit early if auto-login didn't redirect
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific error types with user-friendly messages
      const errorStatus = error?.status || error?.statusCode;
      if (errorStatus === 429) {
        toast.error('Too many registration attempts. Please wait a moment and try again.', {
          toastId: 'rate-limit-error',
          autoClose: 5000
        });
        // Reset refs so user can retry immediately after a short delay
        setTimeout(() => {
          isSubmittingRef.current = false;
          lastSubmissionTimeRef.current = 0;
        }, 2000); // 2 second delay before allowing retry
      } else if (errorStatus === 401 || errorStatus === 403) {
        toast.error(error.message || 'Registration failed. Please try again.', {
          toastId: 'auth-error'
        });
        // Reset ref on auth errors so user can retry
        isSubmittingRef.current = false;
        lastSubmissionTimeRef.current = 0;
      } else {
        toast.error(error.message || 'Registration failed. Please try again.', {
          toastId: 'registration-error'
        });
        // Reset ref on other errors so user can retry
        isSubmittingRef.current = false;
        lastSubmissionTimeRef.current = 0;
      }
    } finally {
      setLoading(false);
      // Note: isSubmittingRef is reset on errors (handled above) or remains true on success
      // Add a safety timeout to reset ref after 10 seconds if something goes wrong
      // This prevents the form from being permanently disabled
      setTimeout(() => {
        if (isSubmittingRef.current && !loading) {
          console.warn('SignupPage: Auto-resetting isSubmittingRef after timeout');
          isSubmittingRef.current = false;
          lastSubmissionTimeRef.current = 0;
        }
      }, 10000);
    }
  };

  // Plans fetching removed - signup is now free only

  // Plan-related functions removed - signup is now free only

  // Initialize Google Sign-In when component mounts
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('Google Client ID not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env');
      return;
    }

    const initializeGoogleSignIn = () => {
      if (typeof window !== 'undefined' && window.google && window.google.accounts && !googleInitialized.current) {
        try {
          // Initialize Google Identity Services for ID token (popup-based, doesn't require FedCM)
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleCredential,
            // Disable One Tap to avoid FedCM issues
            auto_select: false,
            // Use popup flow instead of FedCM
            use_fedcm_for_prompt: false,
          });
          
          googleInitialized.current = true;
          setIsGoogleReady(true);
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
        }
      }
    };

    // Check if script already loaded
    const existingScript = document.getElementById('google-oauth-sdk');
    if (existingScript) {
      // Script already exists, just initialize
      if (typeof window !== 'undefined' && window.google) {
        initializeGoogleSignIn();
      } else {
        // Wait for script to be ready
        const checkInterval = setInterval(() => {
          if (typeof window !== 'undefined' && window.google) {
            initializeGoogleSignIn();
            clearInterval(checkInterval);
          }
        }, 100);
        
        setTimeout(() => clearInterval(checkInterval), 10000);
      }
      return;
    }

    // Load Google OAuth SDK script
    const script = document.createElement('script');
    script.id = 'google-oauth-sdk';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google OAuth SDK loaded for SignUp');
      initializeGoogleSignIn();
    };
    script.onerror = () => {
      console.error('Failed to load Google OAuth SDK');
    };
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Script cleanup is handled by React
    };
  }, [GOOGLE_CLIENT_ID, handleGoogleCredential, inviteId]);

  const handleGoogleSignup = () => {
    // Prevent multiple error messages
    if (!GOOGLE_CLIENT_ID) {
      if (!errorShownRef.current) {
        console.error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file');
        toast.error('Google Sign-In is not configured. Please contact support or use email signup.', {
          toastId: 'google-config-error', // Prevent duplicate toasts
        });
        errorShownRef.current = true;
        // Reset after 5 seconds to allow showing again if needed
        setTimeout(() => {
          errorShownRef.current = false;
        }, 5000);
      }
      return;
    }

    if (typeof window === 'undefined' || !window.google) {
      toast.info('Google Sign-In is loading. Please wait a moment and try again.', {
        toastId: 'google-loading',
      });
      return;
    }

    // Use a popup-based approach that doesn't rely on FedCM
    try {
      // Create a hidden button element and render Google button on it
      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'absolute';
      buttonContainer.style.left = '-9999px';
      buttonContainer.style.opacity = '0';
      buttonContainer.style.pointerEvents = 'none';
      buttonContainer.style.width = '1px';
      buttonContainer.style.height = '1px';
      document.body.appendChild(buttonContainer);

      // Render Google button with explicit settings to avoid FedCM
      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signup_with',
        callback: handleGoogleCredential,
        // Explicitly disable FedCM
        use_fedcm_for_prompt: false,
      });

      // Find and click the button after it's rendered
      setTimeout(() => {
        const googleButton = buttonContainer.querySelector('div[role="button"]');
        if (googleButton) {
          // Use native click method
          googleButton.click();
        } else {
          // If button not found after delay, try again or show error
          setTimeout(() => {
            const retryButton = buttonContainer.querySelector('div[role="button"]');
            if (retryButton) {
              retryButton.click();
            } else {
              toast.error('Google Sign-In button failed to load. Please refresh the page or use email signup.', {
                toastId: 'google-button-error',
                autoClose: 5000,
              });
              // Cleanup
              if (document.body.contains(buttonContainer)) {
                document.body.removeChild(buttonContainer);
              }
            }
          }, 500);
        }
        
        // Cleanup after a delay
        setTimeout(() => {
          if (document.body.contains(buttonContainer)) {
            document.body.removeChild(buttonContainer);
          }
        }, 10000);
      }, 200);
    } catch (error) {
      console.error('Error triggering Google Sign-In:', error);
      
      // Provide helpful error message about FedCM
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('FedCM') || errorMessage.includes('fedcm')) {
        toast.error(
          'FedCM (Federated Credential Management) is disabled in your browser. ' +
          'Please enable it in Site Settings or use email signup instead.',
          {
            toastId: 'google-fedcm-error',
            autoClose: 10000,
          }
        );
      } else {
        toast.error('Failed to open Google Sign-In. Please try again or use email signup.', {
          toastId: 'google-error',
        });
      }
    }
  };

  const handleAppleSignup = () => {
    toast.info('Apple signup would be implemented with OAuth');
  };

  return (
    <div className="signup-page">
      <Navbar />
      
      {loading && <LoadingSpinner fullScreen text="Creating your account..." />}
      
      <Container className="signup-container">
        <div className="signup-header">
          <h1>Create Your Account</h1>
        </div>

        <div className="signup-form-wrapper">
          <Form onSubmit={handleSubmit(onSubmit)} className="signup-form">
            {/* Name Fields */}
            <div className="form-section">
              <label className="form-label-custom">Your Name <span className="required-asterisk">*</span></label>
              <Row>
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Control
                    type="text"
                    placeholder="First name"
                    className={`form-input ${errors.firstName ? 'is-invalid' : ''}`}
                    {...register('firstName', { 
                      required: 'First name is required',
                      minLength: { value: 2, message: 'At least 2 characters' }
                    })}
                  />
                  {errors.firstName && (
                    <div className="error-message">{errors.firstName.message}</div>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Last name"
                    className={`form-input ${errors.lastName ? 'is-invalid' : ''}`}
                    {...register('lastName', { 
                      required: 'Last name is required',
                      minLength: { value: 2, message: 'At least 2 characters' }
                    })}
                  />
                  {errors.lastName && (
                    <div className="error-message">{errors.lastName.message}</div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Email Field */}
            <div className="form-section">
              <label className="form-label-custom">Email Address <span className="required-asterisk">*</span></label>
              <Form.Control
                type="email"
                placeholder="you@example.com"
                className={`form-input ${errors.email ? 'is-invalid' : ''}`}
                disabled={!!inviteEmail}
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {errors.email && (
                <div className="error-message">{errors.email.message}</div>
              )}
            </div>

            {/* Password Field */}
            <div className="form-section">
              <label className="form-label-custom">Password <span className="required-asterisk">*</span></label>
              <div className="password-input-wrapper">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  className={`form-input ${errors.password ? 'is-invalid' : ''}`}
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: { value: 8, message: 'At least 8 characters' },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: 'Must include uppercase, lowercase, and number'
                    }
                  })}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <div className="error-message">{errors.password.message}</div>
              )}
            </div>

            <CustomButton
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={loading}
              className="signup-submit"
            >
              Create Account & Continue
            </CustomButton>

            {/* Social Divider */}
            <div className="signup-divider">
              <span>or sign up with</span>
            </div>

            {/* Social Signup Buttons */}
            <div className="social-signup">
              {/* Google Sign-Up Button */}
              <button
                type="button"
                className={`social-btn ${inviteId ? 'social-btn-disabled' : ''}`}
                onClick={inviteId ? undefined : handleGoogleSignup}
                disabled={loading || !isGoogleReady || !!inviteId}
                title={inviteId ? 'Social login is disabled for invite links. Please use email signup.' : undefined}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google
              </button>
              <button
                type="button"
                className={`social-btn ${inviteId ? 'social-btn-disabled' : ''}`}
                onClick={inviteId ? undefined : handleAppleSignup}
                disabled={!!inviteId}
                title={inviteId ? 'Social login is disabled for invite links. Please use email signup.' : undefined}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z"/>
                </svg>
                Apple
              </button>
            </div>

            {/* Footer */}
            <div className="signup-footer">
              Already have an account? <Link to="/signin">Sign in</Link>
            </div>
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default SignupPage;
